import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import {
  Calendar, Clock, MapPin, Users, Wallet, Sparkles,
  ArrowRight, ArrowLeft
} from "lucide-react";
import { format } from "date-fns";

const vibeOptions = [
  { label: "Rooftop", icon: "🌃" },
  { label: "Live Music", icon: "🎵" },
  { label: "DJ / EDM", icon: "🎧" },
  { label: "Craft Beer", icon: "🍺" },
  { label: "Cocktails", icon: "🍸" },
  { label: "VIP / Bottle", icon: "🥂" },
  { label: "Casual", icon: "😎" },
  { label: "Instagram-worthy", icon: "📸" },
  { label: "Dancing", icon: "💃" },
  { label: "Chill Lounge", icon: "🛋️" },
];

const CreateKrew = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [eventDate, setEventDate] = useState("");
  const [eventTime, setEventTime] = useState("21:00");
  const [city, setCity] = useState("Bangalore");
  const [area, setArea] = useState("");
  const [vibes, setVibes] = useState<string[]>([]);
  const [budgetPerPerson, setBudgetPerPerson] = useState(3000);
  const [maxMembers, setMaxMembers] = useState(6);
  const [commitmentAmount, setCommitmentAmount] = useState(500);

  const toggleVibe = (vibe: string) => {
    setVibes((prev) =>
      prev.includes(vibe) ? prev.filter((v) => v !== vibe) : [...prev, vibe]
    );
  };

  const handleCreate = async () => {
    if (!user) return;
    setLoading(true);

    try {
      const deadlineDate = new Date(eventDate);
      deadlineDate.setDate(deadlineDate.getDate() - 2);
      deadlineDate.setHours(18, 0, 0, 0);

      const { data, error } = await supabase
        .from("krews")
        .insert({
          creator_id: user.id,
          title,
          description,
          event_date: eventDate,
          event_time: eventTime,
          city,
          area,
          vibe: vibes,
          budget_per_person: budgetPerPerson,
          max_members: maxMembers,
          commitment_amount: commitmentAmount,
          commitment_deadline: deadlineDate.toISOString(),
        })
        .select()
        .single();

      if (error) throw error;

      // Auto-add creator as member (trigger auto-commits creator)
      await supabase.from("krew_members").insert({
        krew_id: data.id,
        user_id: user.id,
      });

      toast({ title: "Krew created! 🎉", description: "Share the invite link with your squad." });
      navigate(`/krew/${data.id}`);
    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl">
      <div className="mb-8">
        <button onClick={() => navigate(-1)} className="mb-4 flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        <h1 className="text-3xl font-bold text-foreground">Create a <span className="text-gradient-neon">Krew</span></h1>
        <p className="mt-1 text-muted-foreground font-body">Plan your next legendary night in under 2 minutes</p>
      </div>

      {/* Progress */}
      <div className="mb-8 flex gap-2">
        {[1, 2, 3].map((s) => (
          <div key={s} className={`h-1.5 flex-1 rounded-full transition-all ${s <= step ? "bg-primary" : "bg-muted"}`} />
        ))}
      </div>

      <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-6">
        {step === 1 && (
          <>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-foreground">What's the plan?</label>
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Saturday night out — Indiranagar"
                className="w-full rounded-xl border border-border bg-muted px-4 py-3 text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-foreground">Description (optional)</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Birthday celebration, team outing, just vibes..."
                rows={3}
                className="w-full rounded-xl border border-border bg-muted px-4 py-3 text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary resize-none"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="mb-1.5 flex items-center gap-2 text-sm font-medium text-foreground">
                  <Calendar className="h-4 w-4 text-primary" /> Date
                </label>
                <input
                  type="date"
                  value={eventDate}
                  onChange={(e) => setEventDate(e.target.value)}
                  min={format(new Date(), "yyyy-MM-dd")}
                  className="w-full rounded-xl border border-border bg-muted px-4 py-3 text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                />
              </div>
              <div>
                <label className="mb-1.5 flex items-center gap-2 text-sm font-medium text-foreground">
                  <Clock className="h-4 w-4 text-primary" /> Time
                </label>
                <input
                  type="time"
                  value={eventTime}
                  onChange={(e) => setEventTime(e.target.value)}
                  className="w-full rounded-xl border border-border bg-muted px-4 py-3 text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="mb-1.5 flex items-center gap-2 text-sm font-medium text-foreground">
                  <MapPin className="h-4 w-4 text-primary" /> City
                </label>
                <select
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  className="w-full rounded-xl border border-border bg-muted px-4 py-3 text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                >
                  <option>Bangalore</option>
                  <option>Mumbai</option>
                  <option>Delhi</option>
                  <option>Hyderabad</option>
                  <option>Chennai</option>
                  <option>Pune</option>
                  <option>Goa</option>
                </select>
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-foreground">Area</label>
                <input
                  value={area}
                  onChange={(e) => setArea(e.target.value)}
                  placeholder="Indiranagar, Koramangala..."
                  className="w-full rounded-xl border border-border bg-muted px-4 py-3 text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                />
              </div>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <div>
              <label className="mb-3 block text-sm font-medium text-foreground">What's the vibe?</label>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                {vibeOptions.map((v) => (
                  <button
                    key={v.label}
                    type="button"
                    onClick={() => toggleVibe(v.label.toLowerCase())}
                    className={`flex items-center gap-2 rounded-xl border px-4 py-3 text-sm font-medium transition-all ${
                      vibes.includes(v.label.toLowerCase())
                        ? "border-primary bg-primary/10 text-primary"
                        : "border-border bg-card text-muted-foreground hover:border-primary/30"
                    }`}
                  >
                    <span>{v.icon}</span>
                    {v.label}
                  </button>
                ))}
              </div>
            </div>
          </>
        )}

        {step === 3 && (
          <>
            <div>
              <label className="mb-1.5 flex items-center gap-2 text-sm font-medium text-foreground">
                <Users className="h-4 w-4 text-primary" /> Max group size
              </label>
              <div className="flex items-center gap-4">
                <input
                  type="range"
                  min={2}
                  max={20}
                  value={maxMembers}
                  onChange={(e) => setMaxMembers(Number(e.target.value))}
                  className="flex-1 accent-primary"
                />
                <span className="w-12 text-center text-2xl font-bold text-foreground">{maxMembers}</span>
              </div>
            </div>
            <div>
              <label className="mb-1.5 flex items-center gap-2 text-sm font-medium text-foreground">
                <Wallet className="h-4 w-4 text-secondary" /> Budget per person
              </label>
              <div className="flex items-center gap-4">
                <input
                  type="range"
                  min={500}
                  max={15000}
                  step={500}
                  value={budgetPerPerson}
                  onChange={(e) => setBudgetPerPerson(Number(e.target.value))}
                  className="flex-1 accent-secondary"
                />
                <span className="w-20 text-right text-2xl font-bold text-gradient-gold">₹{budgetPerPerson.toLocaleString()}</span>
              </div>
            </div>
            <div>
              <label className="mb-1.5 flex items-center gap-2 text-sm font-medium text-foreground">
                <Sparkles className="h-4 w-4 text-primary" /> Commitment deposit
              </label>
              <p className="mb-3 text-xs text-muted-foreground">Refundable if they show up. Penalty if they bail.</p>
              <div className="flex gap-3">
                {[250, 500, 1000, 2000].map((amt) => (
                  <button
                    key={amt}
                    type="button"
                    onClick={() => setCommitmentAmount(amt)}
                    className={`flex-1 rounded-xl border py-3 text-sm font-semibold transition-all ${
                      commitmentAmount === amt
                        ? "border-primary bg-primary/10 text-primary"
                        : "border-border text-muted-foreground hover:border-primary/30"
                    }`}
                  >
                    ₹{amt}
                  </button>
                ))}
              </div>
            </div>

            {/* Summary */}
            <div className="rounded-2xl border border-primary/20 bg-primary/5 p-5">
              <h3 className="mb-3 text-sm font-semibold text-primary">Krew Summary</h3>
              <div className="space-y-2 text-sm text-foreground">
                <p><strong>{title || "Untitled Krew"}</strong></p>
                <p>📅 {eventDate ? format(new Date(eventDate), "EEE, MMM d") : "TBD"} at {eventTime}</p>
                <p>📍 {area || city}</p>
                <p>👥 Up to {maxMembers} people</p>
                <p>💰 ₹{budgetPerPerson.toLocaleString()}/person · Total ₹{(budgetPerPerson * maxMembers).toLocaleString()}</p>
                <p>🔒 ₹{commitmentAmount} commitment deposit</p>
              </div>
            </div>
          </>
        )}
      </motion.div>

      {/* Navigation */}
      <div className="mt-8 flex justify-between">
        {step > 1 ? (
          <button
            onClick={() => setStep(step - 1)}
            className="flex items-center gap-2 rounded-xl border border-border px-6 py-3 text-sm font-medium text-foreground hover:bg-muted"
          >
            <ArrowLeft className="h-4 w-4" /> Back
          </button>
        ) : (
          <div />
        )}
        {step < 3 ? (
          <button
            onClick={() => setStep(step + 1)}
            disabled={step === 1 && (!title || !eventDate)}
            className="flex items-center gap-2 rounded-xl bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-all glow-neon hover:scale-[1.02] disabled:opacity-50"
          >
            Next <ArrowRight className="h-4 w-4" />
          </button>
        ) : (
          <button
            onClick={handleCreate}
            disabled={loading || !title || !eventDate}
            className="flex items-center gap-2 rounded-xl bg-primary px-8 py-3 text-sm font-semibold text-primary-foreground transition-all glow-neon hover:scale-[1.02] disabled:opacity-50"
          >
            {loading ? (
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary-foreground border-t-transparent" />
            ) : (
              <>
                Create Krew <Sparkles className="h-4 w-4" />
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
};

export default CreateKrew;
